import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { CircleNotch, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleCallback } = useAuth();
  const [error, setError] = useState(null);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Guard against StrictMode double-effect
    if (hasProcessedRef.current) return;
    hasProcessedRef.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (errorParam) {
      setError(errorDescription || errorParam);
      return;
    }

    if (!code || !state) {
      setError('Missing authorization code or state.');
      return;
    }

    handleCallback(code, state)
      .then(() => {
        toast.success('Signed in successfully');
        navigate('/', { replace: true });
      })
      .catch((err) => {
        console.error('Callback error:', err);
        setError(err.response?.data?.detail || 'Authentication failed. Please try again.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-slate-50" data-testid="auth-callback-page">
      <div className="w-full max-w-md text-center">
        {error ? (
          <div className="bg-white border border-red-200 rounded-lg p-8" data-testid="auth-error">
            <div className="inline-flex bg-red-50 p-3 rounded-lg mb-4">
              <WarningCircle size={32} weight="duotone" className="text-red-600" />
            </div>
            <h1 className="text-2xl font-medium text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Sign-in failed
            </h1>
            <p className="text-base text-slate-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="btn-primary px-5 py-2 rounded-md text-sm font-medium"
              data-testid="return-login-button"
            >
              Return to Login
            </button>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg p-8" data-testid="auth-loading">
            <CircleNotch size={40} className="text-[#002FA7] animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-medium text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Completing sign-in
            </h1>
            <p className="text-base text-slate-600">Verifying your Microsoft account...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
